import { useCompaniesForReport, useLearningProgressIndustries } from "@/api/services/main/learning-progress";
import { TCompanyForReportOutputDto } from "@/api/services/main/learning-progress/dtos/get-companies-for-report.dto";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { Pagination } from "@/components/elements/Pagination";
import { EMPTY_STR } from "@/constants/string";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { usePagination } from "@/hooks/usePagination";
import { CompaniesForReportTable } from "@/features/learning-progress/components/report/CompaniesForReportTable";
import { ReportListTop } from "@/features/learning-progress/components/report/ReportListTop";
import { divider } from "@/theme/colors";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";
import { Stack, Typography } from "@mui/material";
import { FC, useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

export const LearningProgressReportPage: FC = () => {
  const { industryId } = useParams<{ industryId: string }>();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const pagReq = usePagination();

  const numericIndustryId = industryId ? Number(industryId) : 0;
  const currentPage = pagReq.page || START_PAGE;
  const perPage = pagReq.perPage || DEFAULT_PER_PAGE;

  const { data: industriesData } = useLearningProgressIndustries();
  const industry = industriesData?.data?.find((item) => item.id === numericIndustryId);

  // 入力欄のドラフト値と、実際に検索へ反映済みの値を分離する。検索ボタン押下で反映する。
  const [keyword, setKeyword] = useState<string>("");
  const [appliedKeyword, setAppliedKeyword] = useState<string>("");

  const trimmedKeyword = appliedKeyword.trim();
  const { data: companiesData, isLoading } = useCompaniesForReport({
    industryId: numericIndustryId,
    page: currentPage,
    perPage,
    sorts: pagReq.sorts,
    ...(trimmedKeyword ? { keyword: trimmedKeyword } : {}),
  });

  const rows = companiesData?.data ?? [];
  const pagination = companiesData?.pagination;

  const resetToFirstPage = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "1");
        return next;
      },
      { replace: true },
    );
  };

  const handleSearch = () => {
    setAppliedKeyword(keyword);
    resetToFirstPage();
  };

  const handleClearKeyword = () => {
    setKeyword("");
    setAppliedKeyword("");
    resetToFirstPage();
  };

  const handleChangeKeyword = useCallback((value: string) => {
    setKeyword(value);

    if (!value.trim()) {
      setAppliedKeyword("");
      resetToFirstPage();
    }
  }, [resetToFirstPage])

  const handleClickCompany = useCallback((row: TCompanyForReportOutputDto) => {
    navigate(
      getDynamicRoute(AppRoutes.learningProgressReportCompany, {
        industryId: String(numericIndustryId),
        companyId: String(row.id),
      }),
    );
  }, [navigate, numericIndustryId]);

  const handleGoBack = () =>
    navigate(getDynamicRoute(AppRoutes.learningProgressDetail, { industryId: String(numericIndustryId) }));

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <Typography variant="head20Bold">学習進捗報告書の作成 - {industry?.name || EMPTY_STR.TEXT}</Typography>
      </Stack>

      <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high}>
        <Stack gap={3}>
          <ReportListTop
            pagination={pagination}
            keyword={keyword}
            onChangeKeyword={handleChangeKeyword}
            onSubmit={handleSearch}
            onClear={handleClearKeyword}
          />
          <CompaniesForReportTable rows={rows} isLoading={isLoading} onClickCompany={handleClickCompany} />
          <Pagination totalPage={pagination?.totalPage ?? 1} />
        </Stack>
      </InnerBox>

      <FooterContainer>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button variant="ghost" size="sm" onClick={handleGoBack}>
            戻る
          </Button>
        </Stack>
      </FooterContainer>
    </Stack>
  );
};
